import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* @section: constants */
const WHATSAPP_URL = "https://wa.me/5565992179821";
const MAPS_URL =
  "https://maps.app.goo.gl/MV6aDdD6omCKNkh58";

const LOGO_URL =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/logo_icon_only_d58ba4f02a10447a893ccb91eac777c2.png";

const FOTO1 =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/Foto%201_6156df78b6204721810655e381750d6a.jpg";
const FOTO2 =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/Foto%202_8d7b7c7e016241e18f09b7f86788e974.jpg";

/* @section: colors */
const C = {
  bg: "#EDE3D3",
  bgCard: "#E2D4BF",
  gold: "#C8A84B",
  goldDark: "#a88830",
  plum: "#5D2E5F",
  plumDark: "#3A1E3C",
  text: "#302C2B",
  textMuted: "#7A736F",
  green: "#3D7A46",
};

/* @section: animations */
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}

function trackWhatsAppClick(location: string) {
  if (typeof window === "undefined") return;

  if (typeof window.gtag === "function") {
    window.gtag("event", "click_whatsapp", {
      event_category: "engagement",
      event_label: location,
      destination: "whatsapp",
    });
  }

  if (typeof window.fbq === "function") {
    window.fbq("trackCustom", "ClickWhatsApp", {
      location,
      destination: "whatsapp",
    });
  }
}

/* ------------------------------------------------------------------ */
/*  WHATSAPP FLOATING BUTTON                                           */
/* @section: whatsapp-button */
/* ------------------------------------------------------------------ */
function WhatsAppButton() {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackWhatsAppClick("floating_button")}
      aria-label="Falar no WhatsApp"
      style={{
        position: "fixed",
        bottom: "28px",
        right: "24px",
        zIndex: 9999,
        width: "60px",
        height: "60px",
        borderRadius: "50%",
        backgroundColor: C.green,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 18px rgba(61,122,70,0.45)",
        cursor: "pointer",
        textDecoration: "none",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.transform = "scale(1.08)";
        (e.currentTarget as HTMLElement).style.boxShadow = "0 6px 24px rgba(61,122,70,0.6)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.transform = "scale(1)";
        (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 18px rgba(61,122,70,0.45)";
      }}
    >
      {/* WhatsApp SVG icon */}
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M16 2.5C8.544 2.5 2.5 8.544 2.5 16c0 2.38.638 4.61 1.75 6.537L2.5 29.5l7.18-1.722A13.456 13.456 0 0016 29.5C23.456 29.5 29.5 23.456 29.5 16S23.456 2.5 16 2.5z"
          fill="white"
          fillOpacity="0.18"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16 4C9.373 4 4 9.373 4 16c0 2.25.605 4.36 1.664 6.176L4 28l5.985-1.636A11.96 11.96 0 0016 28c6.627 0 12-5.373 12-12S22.627 4 16 4zm-3.47 6.667c-.19-.47-.39-.48-.57-.488L11.4 10.17c-.19 0-.49.072-.748.358-.258.286-.984.962-.984 2.345 0 1.383 1.007 2.72 1.147 2.907.14.187 1.94 3.087 4.77 4.203 2.36.932 2.834.747 3.345.7.51-.047 1.647-.673 1.88-1.322.234-.65.234-1.207.164-1.323-.07-.116-.258-.187-.54-.327-.28-.14-1.647-.813-1.903-.906-.257-.093-.444-.14-.632.14-.187.28-.725.906-.889 1.093-.163.187-.327.21-.607.07-.28-.14-1.182-.436-2.252-1.39-.833-.742-1.394-1.658-1.558-1.938-.163-.28-.017-.432.123-.57.126-.125.28-.327.42-.49.14-.163.187-.28.28-.467.094-.186.047-.35-.023-.49-.07-.14-.608-1.53-.843-2.086z"
          fill="white"
        />
      </svg>
    </a>
  );
}

/* ------------------------------------------------------------------ */
/*  HEADER / NAV                                                        */
/* @section: header */
/* ------------------------------------------------------------------ */
function Header() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 480);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        backgroundColor: "rgba(250,247,244,0.92)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(93,46,95,0.08)",
        padding: "0 24px",
        height: "68px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {/* @section: header-brand */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", textDecoration: "none" }}>
        {/* Logo: ícone dourado isolado */}
        <img
          src={LOGO_URL}
          alt="Logo Dra. Thayna Ferreira"
          style={{
            height: "48px",
            width: "auto",
            objectFit: "contain",
            flexShrink: 0,
            filter: "drop-shadow(0 1px 6px rgba(200,168,75,0.45))",
          }}
        />
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? "15px" : "17px", fontWeight: 600, color: C.plum, letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
            Dra. Thayna Ferreira
          </span>
          {!isMobile && (
            <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "10px", fontWeight: 500, color: C.textMuted, letterSpacing: "0.12em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
              Cirurgiã-Dentista · CRO-MT 7999
            </span>
          )}
        </div>
      </div>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackWhatsAppClick("header_agendar")}
        style={{
          backgroundColor: C.gold,
          color: "#fff",
          padding: "8px 20px",
          borderRadius: "100px",
          fontSize: "12px",
          fontWeight: 600,
          fontFamily: "'Montserrat', sans-serif",
          letterSpacing: "0.08em",
          textDecoration: "none",
          textTransform: "uppercase",
          transition: "background-color 0.2s ease",
        }}
        onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.backgroundColor = C.goldDark)}
        onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.backgroundColor = C.gold)}
      >
        Agendar
      </a>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/*  HERO SECTION                                                        */
/* @section: hero */
/* ------------------------------------------------------------------ */
function HeroSection() {
  return (
    <section
      id="hero"
      style={{
        minHeight: "100vh",
        backgroundColor: C.bg,
        display: "flex",
        alignItems: "center",
        paddingTop: "68px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Decorative circle */}
      <div
        style={{
          position: "absolute",
          top: "-120px",
          right: "-120px",
          width: "480px",
          height: "480px",
          borderRadius: "50%",
          background: `radial-gradient(circle, rgba(93,46,95,0.07) 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "60px 24px",
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "48px",
          alignItems: "center",
        }}
        className="hero-grid"
      >
        {/* Text side */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          style={{ display: "flex", flexDirection: "column", gap: "24px" }}
        >
          <motion.div variants={fadeUp}>
            <span
              style={{
                display: "inline-block",
                fontSize: "11px",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: C.gold,
                backgroundColor: "rgba(200,168,75,0.1)",
                padding: "6px 16px",
                borderRadius: "100px",
                marginBottom: "4px",
              }}
            >
              Harmonização Facial · Cuiabá, MT
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(32px, 4vw, 52px)",
              fontWeight: 600,
              color: C.text,
              lineHeight: 1.22,
              margin: 0,
            }}
          >
            Recupere o aspecto descansado do seu rosto e realce sua beleza com naturalidade.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "16px",
              color: C.textMuted,
              lineHeight: 1.7,
              margin: 0,
            }}
          >
            Procedimentos personalizados de Harmonização e Estética Facial em Cuiabá. Unindo o domínio da anatomia,
            segurança clínica e sensibilidade artística.
          </motion.p>

          <motion.div variants={fadeUp} style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("hero_cta")}
              style={{
                display: "inline-block",
                backgroundColor: C.gold,
                color: "#fff",
                padding: "16px 32px",
                borderRadius: "100px",
                fontSize: "13px",
                fontWeight: 700,
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "0.1em",
                textDecoration: "none",
                textTransform: "uppercase",
                boxShadow: "0 4px 20px rgba(200,168,75,0.35)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = C.goldDark;
                (e.currentTarget as HTMLElement).style.boxShadow = "0 6px 28px rgba(200,168,75,0.5)";
                (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = C.gold;
                (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 20px rgba(200,168,75,0.35)";
                (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
              }}
            >
              Agendar minha avaliação facial
            </a>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            variants={fadeUp}
            style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginTop: "8px" }}
          >
            {["CRO-MT 7999", "+8 anos de experiência", "Resultados naturais"].map((badge) => (
              <div
                key={badge}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "12px",
                  fontFamily: "'Montserrat', sans-serif",
                  color: C.textMuted,
                  fontWeight: 500,
                }}
              >
                <span style={{ color: C.plum, fontSize: "14px" }}>✦</span>
                {badge}
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Photo side */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, x: 30 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          style={{ position: "relative", display: "flex", justifyContent: "center" }}
        >
          <div
            style={{
              position: "absolute",
              inset: "-16px",
              borderRadius: "40px",
              background: `linear-gradient(135deg, rgba(93,46,95,0.12) 0%, rgba(200,168,75,0.08) 100%)`,
              zIndex: 0,
            }}
          />
          <img
            src={FOTO1}
            alt="Dra. Thayna Ferreira – Especialista em Harmonização Facial"
            style={{
              width: "100%",
              maxWidth: "460px",
              height: "580px",
              objectFit: "cover",
              objectPosition: "center top",
              borderRadius: "32px",
              position: "relative",
              zIndex: 1,
              boxShadow: "0 24px 60px rgba(93,46,95,0.18)",
            }}
          />
          {/* Gold accent line */}
          <div
            style={{
              position: "absolute",
              bottom: "24px",
              right: "-12px",
              width: "4px",
              height: "120px",
              backgroundColor: C.gold,
              borderRadius: "4px",
              zIndex: 2,
            }}
          />
        </motion.div>
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
          }
          .hero-grid > div:last-child {
            order: -1;
          }
          .hero-grid > div:last-child img {
            height: 360px !important;
          }
        }
      `}</style>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  SYMPTOMS SECTION                                                    */
/* @section: symptoms */
/* ------------------------------------------------------------------ */
const symptoms = [
  { icon: "🌙", label: "Olheiras e aspecto de cansaço constante" },
  { icon: "〰️", label: "Linhas de expressão que marcam a maquiagem" },
  { icon: "🌿", label: "Perda de volume nos lábios ou contorno do rosto" },
  { icon: "💧", label: "Sensação de flacidez ou pele sem viço" },
];

function SymptomsSection() {
  return (
    <section
      id="sintomas"
      style={{ backgroundColor: C.bgCard, padding: "96px 24px" }}
    >
      <div style={{ maxWidth: "760px", margin: "0 auto", textAlign: "center" }}>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          <motion.h2
            variants={fadeUp}
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(26px, 3.5vw, 42px)",
              fontWeight: 600,
              color: C.text,
              lineHeight: 1.28,
              marginBottom: "20px",
            }}
          >
            O espelho parou de refletir como você se sente por dentro?
          </motion.h2>

          <motion.p
            variants={fadeUp}
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "16px",
              color: C.textMuted,
              lineHeight: 1.7,
              marginBottom: "56px",
            }}
          >
            Muitas vezes, a nossa pele transmite um cansaço que não condiz com a nossa energia. Você percebe alguns
            destes sinais?
          </motion.p>

          <motion.div
            variants={stagger}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "20px",
              textAlign: "left",
            }}
          >
            {symptoms.map((s) => (
              <motion.div
                key={s.label}
                variants={fadeUp}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  backgroundColor: C.bg,
                  borderRadius: "16px",
                  padding: "20px 24px",
                  boxShadow: "0 2px 12px rgba(93,46,95,0.06)",
                  border: "1px solid rgba(93,46,95,0.07)",
                }}
              >
                <span style={{ fontSize: "24px", flexShrink: 0 }}>{s.icon}</span>
                <span
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "14px",
                    color: C.text,
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  {s.label}
                </span>
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            variants={fadeUp}
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "15px",
              color: C.plum,
              fontWeight: 600,
              marginTop: "48px",
              fontStyle: "italic",
            }}
          >
            Se identificou? Existe uma solução segura, natural e personalizada para você.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  PROCEDURES SECTION                                                  */
/* @section: procedures */
/* ------------------------------------------------------------------ */
const procedures = [
  {
    icon: "✦",
    title: "Toxina Botulínica (Botox)",
    desc: "Suavize rugas, levante o olhar e previna o envelhecimento precoce.",
    detail: "Tratamento preciso que relaxa a musculatura facial, atenuando linhas de expressão e proporcionando um olhar mais jovem e descansado. Resultado natural com segurança clínica comprovada.",
  },
  {
    icon: "◆",
    title: "Preenchimento com Ácido Hialurônico",
    desc: "Devolva o volume perdido e embeleze os contornos faciais.",
    detail: "Substância naturalmente presente no organismo que restaura o volume e hidratação dos tecidos. Ideal para lábios, olheiras, maçãs do rosto e sulcos nasolabiais.",
  },
  {
    icon: "❋",
    title: "Bioestimuladores de Colágeno",
    desc: "Trate a flacidez de dentro para fora, devolvendo firmeza à pele.",
    detail: "Estimula a produção natural de colágeno pelo próprio organismo. Promove firmeza, elasticidade e melhora progressiva da qualidade da pele ao longo dos meses.",
  },
];

function ProceduresSection() {
  return (
    <section id="procedimentos" style={{ backgroundColor: C.bg, padding: "96px 24px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: "56px" }}>
            <span
              style={{
                display: "inline-block",
                fontSize: "11px",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: C.gold,
                marginBottom: "12px",
              }}
            >
              Serviços
            </span>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(26px, 3.5vw, 42px)",
                fontWeight: 600,
                color: C.text,
                lineHeight: 1.28,
                margin: 0,
              }}
            >
              Tratamentos sob medida para você
            </h2>
          </motion.div>

          <motion.div
            variants={stagger}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
              gap: "28px",
            }}
          >
            {procedures.map((p) => (
              <motion.div
                key={p.title}
                variants={fadeUp}
                whileHover={{ y: -6, boxShadow: `0 20px 48px rgba(93,46,95,0.14)` }}
                style={{
                  backgroundColor: C.bgCard,
                  borderRadius: "24px",
                  padding: "36px 32px",
                  border: "1px solid rgba(93,46,95,0.07)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  boxShadow: "0 4px 20px rgba(93,46,95,0.06)",
                  transition: "box-shadow 0.3s ease",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "14px",
                    backgroundColor: "rgba(93,46,95,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    color: C.plum,
                    fontWeight: 700,
                  }}
                >
                  {p.icon}
                </div>
                <h3
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "22px",
                    fontWeight: 600,
                    color: C.text,
                    margin: 0,
                    lineHeight: 1.3,
                  }}
                >
                  {p.title}
                </h3>
                <p
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "14px",
                    color: C.text,
                    fontWeight: 600,
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {p.desc}
                </p>
                <p
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "13px",
                    color: C.textMuted,
                    margin: 0,
                    lineHeight: 1.65,
                    flexGrow: 1,
                  }}
                >
                  {p.detail}
                </p>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick(`procedure_card_${p.title.toLowerCase().replace(/\s/g, "_")}`)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    backgroundColor: "transparent",
                    color: C.plum,
                    border: `2px solid ${C.plum}`,
                    padding: "10px 22px",
                    borderRadius: "100px",
                    fontSize: "12px",
                    fontWeight: 700,
                    fontFamily: "'Montserrat', sans-serif",
                    letterSpacing: "0.08em",
                    textDecoration: "none",
                    textTransform: "uppercase",
                    marginTop: "8px",
                    alignSelf: "flex-start",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.backgroundColor = C.plum;
                    el.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.backgroundColor = "transparent";
                    el.style.color = C.plum;
                  }}
                >
                  Saber mais →
                </a>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  ABOUT SECTION                                                       */
/* @section: about */
/* ------------------------------------------------------------------ */
const benefits = [
  "Especialista em Harmonização Facial",
  "Mestre e Doutora em Ciências Odontológicas Integradas",
  "Residência em Saúde Hospitalar",
  "+8 anos de experiência clínica",
];

function AboutSection() {
  return (
    <section id="sobre" style={{ backgroundColor: C.bgCard, padding: "96px 24px" }}>
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "64px",
          alignItems: "center",
        }}
        className="about-grid"
      >
        {/* Photo */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{ position: "relative" }}
        >
          <div
            style={{
              position: "absolute",
              bottom: "-20px",
              left: "-20px",
              width: "60%",
              height: "60%",
              borderRadius: "24px",
              background: `linear-gradient(135deg, rgba(200,168,75,0.15), rgba(93,46,95,0.08))`,
              zIndex: 0,
            }}
          />
          <img
            src={FOTO2}
            alt="Dra. Thayna Ferreira – Cirurgiã-Dentista"
            style={{
              width: "100%",
              height: "540px",
              objectFit: "cover",
              objectPosition: "center top",
              borderRadius: "28px",
              position: "relative",
              zIndex: 1,
              boxShadow: "0 20px 52px rgba(93,46,95,0.16)",
            }}
          />
          {/* CRO badge */}
          <div
            style={{
              position: "absolute",
              bottom: "28px",
              right: "-20px",
              backgroundColor: C.gold,
              color: "#fff",
              borderRadius: "16px",
              padding: "14px 20px",
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "12px",
              fontWeight: 700,
              lineHeight: 1.4,
              zIndex: 2,
              boxShadow: "0 8px 24px rgba(200,168,75,0.4)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "20px", fontWeight: 800, fontFamily: "'Playfair Display', serif" }}>+8</div>
            <div style={{ letterSpacing: "0.06em" }}>ANOS DE<br />EXPERIÊNCIA</div>
          </div>
        </motion.div>

        {/* Text */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          style={{ display: "flex", flexDirection: "column", gap: "24px" }}
        >
          <motion.span
            variants={fadeUp}
            style={{
              display: "inline-block",
              fontSize: "11px",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: C.gold,
            }}
          >
            Sobre a Especialista
          </motion.span>

          <motion.h2
            variants={fadeUp}
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(24px, 3vw, 38px)",
              fontWeight: 600,
              color: C.text,
              lineHeight: 1.25,
              margin: 0,
            }}
          >
            Quem vai cuidar de você
          </motion.h2>

          <motion.div variants={fadeUp}>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "15px",
                fontWeight: 700,
                color: C.plum,
                margin: "0 0 4px",
              }}
            >
              Dra. Thayna Ferreira
            </p>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "13px",
                color: C.textMuted,
                margin: 0,
                letterSpacing: "0.04em",
              }}
            >
              Cirurgiã-Dentista · CRO-MT 7999
            </p>
          </motion.div>

          <motion.p
            variants={fadeUp}
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "15px",
              color: C.textMuted,
              lineHeight: 1.75,
              margin: 0,
            }}
          >
            Apaixonada por realçar o que há de melhor em cada paciente. Com sólida formação acadêmica e clínica, preza
            por um atendimento ético, focado na segurança máxima e no profundo conhecimento anatômico para entregar
            resultados naturais e elegantes.
          </motion.p>

          <motion.ul
            variants={stagger}
            style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {benefits.map((b) => (
              <motion.li
                key={b}
                variants={fadeUp}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: "14px",
                  color: C.text,
                  fontWeight: 500,
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    backgroundColor: C.plum,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  ✓
                </span>
                {b}
              </motion.li>
            ))}
          </motion.ul>

          <motion.div variants={fadeUp}>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("about_cta")}
              style={{
                display: "inline-block",
                backgroundColor: C.plum,
                color: "#fff",
                padding: "16px 32px",
                borderRadius: "100px",
                fontSize: "13px",
                fontWeight: 700,
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "0.08em",
                textDecoration: "none",
                textTransform: "uppercase",
                boxShadow: "0 4px 20px rgba(93,46,95,0.3)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.backgroundColor = C.plumDark;
                el.style.transform = "translateY(-2px)";
                el.style.boxShadow = "0 8px 28px rgba(93,46,95,0.4)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.backgroundColor = C.plum;
                el.style.transform = "translateY(0)";
                el.style.boxShadow = "0 4px 20px rgba(93,46,95,0.3)";
              }}
            >
              Quero conhecer a Dra.
            </a>
          </motion.div>
        </motion.div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .about-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  SOCIAL PROOF + FAQ SECTION                                          */
/* @section: social-proof */
/* ------------------------------------------------------------------ */
/* @section: result-images */
const RESULT_IMAGES = [
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/1_b69cfa46e49649b4a721b5eeebf0c71d.jpeg",
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/2_6e907daed50e421cbfec2b76063b3fb6.jpeg",
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/3_7b1c54c216ae4a1f8b0849fb2dbbc78f.jpeg",
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/4_4642230391ea4139b380ea431a6d0b39.png",
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/5_a6cded384fd645ee8f584803a209c519.png",
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/6_f02ff9656b5041719cd22cd25607541a.jpeg",
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-14/prod_agent_9873e6f6-9009-4342-8ec0-4804bfc93918/7_9b2ae00eea6047658f9924def1424690.png",
];

const faqs = [
  {
    q: "Vai doer?",
    a: "Os procedimentos são realizados com anestesia tópica e técnicas de mínimo desconforto. A maioria das pacientes relata apenas uma leve pressão durante a aplicação. Após o procedimento, eventuais desconfortos passam em poucas horas.",
  },
  {
    q: "Meu rosto vai ficar artificial?",
    a: "Jamais. A filosofia da Dra. Thayna é de resultados completamente naturais, que realçam sua beleza sem alterar sua identidade. Cada procedimento é planejado individualmente para harmonizar seus traços de forma elegante e sutil.",
  },
  {
    q: "Onde fica o consultório?",
    a: "O consultório está localizado em Cuiabá, MT. Clique no link abaixo para ver a localização exata no mapa e planejar sua visita com facilidade.",
    link: MAPS_URL,
    linkLabel: "Ver no Google Maps →",
  },
];

function SocialProofSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [current, setCurrent] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const total = RESULT_IMAGES.length;

  useEffect(() => {
    const updateViewport = () => setIsDesktop(window.innerWidth >= 1024);
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrent((c) => (c + 1) % total);
    }, 4200);
    return () => window.clearInterval(timer);
  }, [total]);

  const visibleCount = isDesktop ? 3 : 1;

  const visibleImages = useMemo(() => {
    return Array.from({ length: visibleCount }, (_, offset) => RESULT_IMAGES[(current + offset) % total]);
  }, [current, total, visibleCount]);

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  return (
    <section id="resultados" style={{ backgroundColor: C.bg, padding: "96px 24px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>

        {/* ── Carrossel ── */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: "48px" }}>
            <span
              style={{
                display: "inline-block",
                fontSize: "11px",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: C.gold,
                marginBottom: "12px",
              }}
            >
              Resultados
            </span>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(26px, 3.5vw, 40px)",
                fontWeight: 600,
                color: C.text,
                lineHeight: 1.28,
                margin: 0,
              }}
            >
              Transformações Reais
            </h2>
          </motion.div>

          <motion.div variants={fadeUp} style={{ position: "relative", marginBottom: "80px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "1fr",
                gap: "18px",
              }}
            >
              {visibleImages.map((url, idx) => (
                <div
                  key={`${url}-${idx}`}
                  style={{
                    borderRadius: "24px",
                    overflow: "hidden",
                    boxShadow: "0 16px 48px rgba(93,46,95,0.12)",
                    backgroundColor: C.bgCard,
                    position: "relative",
                    minHeight: isDesktop ? "420px" : "320px",
                  }}
                >
                  <img
                    src={url}
                    alt={`Resultado ${current + idx + 1} — Dra. Thayna Ferreira`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      display: "block",
                    }}
                  />
                </div>
              ))}
            </div>

            <div
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                backgroundColor: "rgba(58,30,60,0.75)",
                color: "#fff",
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                padding: "4px 12px",
                borderRadius: "100px",
                backdropFilter: "blur(8px)",
                letterSpacing: "0.06em",
              }}
            >
              {current + 1} / {total}
            </div>

            <button
              onClick={prev}
              aria-label="Anterior"
              style={{
                position: "absolute",
                top: "50%",
                left: isDesktop ? "-20px" : "8px",
                transform: "translateY(-50%)",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: "#fff",
                boxShadow: "0 4px 16px rgba(93,46,95,0.18)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                color: C.plum,
                fontWeight: 700,
                transition: "all 0.2s ease",
                zIndex: 10,
              }}
            >
              ‹
            </button>

            <button
              onClick={next}
              aria-label="Próximo"
              style={{
                position: "absolute",
                top: "50%",
                right: isDesktop ? "-20px" : "8px",
                transform: "translateY(-50%)",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: "#fff",
                boxShadow: "0 4px 16px rgba(93,46,95,0.18)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                color: C.plum,
                fontWeight: 700,
                transition: "all 0.2s ease",
                zIndex: 10,
              }}
            >
              ›
            </button>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                marginTop: "20px",
              }}
            >
              {RESULT_IMAGES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  aria-label={`Ir para resultado ${i + 1}`}
                  style={{
                    width: i === current ? "28px" : "10px",
                    height: "10px",
                    borderRadius: "100px",
                    border: "none",
                    backgroundColor: i === current ? C.plum : "rgba(93,46,95,0.25)",
                    cursor: "pointer",
                    padding: 0,
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "16px",
                overflowX: "auto",
                paddingBottom: "4px",
                scrollbarWidth: "none",
                justifyContent: "center",
              }}
            >
              {RESULT_IMAGES.map((url, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  style={{
                    flexShrink: 0,
                    width: "72px",
                    height: "52px",
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: `2px solid ${i === current ? C.gold : "transparent"}`,
                    padding: 0,
                    cursor: "pointer",
                    transition: "border-color 0.2s ease, opacity 0.2s ease",
                    opacity: i === current ? 1 : 0.55,
                    backgroundColor: C.bgCard,
                  }}
                >
                  <img
                    src={url}
                    alt={`Miniatura ${i + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} style={{ textAlign: "center", marginBottom: "40px" }}>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(24px, 3vw, 36px)",
                fontWeight: 600,
                color: C.text,
                margin: 0,
              }}
            >
              Dúvidas Frequentes
            </h2>
          </motion.div>

          <motion.div
            variants={stagger}
            style={{ maxWidth: "760px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {faqs.map((faq, i) => (
              <motion.div
                key={faq.q}
                variants={fadeUp}
                style={{
                  backgroundColor: C.bgCard,
                  borderRadius: "16px",
                  border: `1px solid ${openFaq === i ? "rgba(93,46,95,0.2)" : "rgba(93,46,95,0.07)"}`,
                  overflow: "hidden",
                  transition: "border-color 0.2s ease",
                  boxShadow: openFaq === i ? "0 4px 20px rgba(93,46,95,0.08)" : "none",
                }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    width: "100%",
                    padding: "22px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    gap: "16px",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "18px",
                      fontWeight: 600,
                      color: C.text,
                    }}
                  >
                    {faq.q}
                  </span>
                  <span
                    style={{
                      flexShrink: 0,
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: openFaq === i ? C.plum : "rgba(93,46,95,0.1)",
                      color: openFaq === i ? "#fff" : C.plum,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "16px",
                      fontWeight: 700,
                      transition: "all 0.2s ease",
                      transform: openFaq === i ? "rotate(45deg)" : "rotate(0deg)",
                    }}
                  >
                    +
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      style={{ overflow: "hidden" }}
                    >
                      <div style={{ padding: "0 28px 24px" }}>
                        <p
                          style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "14px",
                            color: C.textMuted,
                            lineHeight: 1.75,
                            margin: 0,
                          }}
                        >
                          {faq.a}
                        </p>
                        {faq.link && (
                          <a
                            href={faq.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-block",
                              marginTop: "12px",
                              color: C.gold,
                              fontFamily: "'Montserrat', sans-serif",
                              fontSize: "13px",
                              fontWeight: 700,
                              textDecoration: "none",
                              letterSpacing: "0.04em",
                            }}
                          >
                            {faq.linkLabel}
                          </a>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{
            textAlign: "center",
            marginTop: "72px",
            padding: "56px 32px",
            backgroundColor: C.bgCard,
            borderRadius: "32px",
            border: "1px solid rgba(93,46,95,0.08)",
          }}
        >
          <h3
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(22px, 2.5vw, 32px)",
              fontWeight: 600,
              color: C.text,
              marginBottom: "16px",
            }}
          >
            Pronta para dar o primeiro passo?
          </h3>
          <p
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "15px",
              color: C.textMuted,
              lineHeight: 1.65,
              maxWidth: "480px",
              margin: "0 auto 32px",
            }}
          >
            Agende sua avaliação facial gratuita e descubra o tratamento ideal para você.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick("mid_cta")}
            style={{
              display: "inline-block",
              backgroundColor: C.gold,
              color: "#fff",
              padding: "18px 40px",
              borderRadius: "100px",
              fontSize: "14px",
              fontWeight: 700,
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "0.1em",
              textDecoration: "none",
              textTransform: "uppercase",
              boxShadow: "0 6px 24px rgba(200,168,75,0.4)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = C.goldDark;
              (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = C.gold;
              (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
            }}
          >
            Agendar minha avaliação facial
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  FOOTER                                                              */
/* @section: footer */
/* ------------------------------------------------------------------ */
function Footer() {
  return (
    <footer style={{ backgroundColor: C.plumDark, padding: "64px 24px 40px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "48px",
            marginBottom: "48px",
          }}
        >
          {/* Brand */}
          <div>
            <div
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "20px",
                fontWeight: 600,
                color: "#fff",
                marginBottom: "12px",
              }}
            >
              Dra. Thayna Ferreira
            </div>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "13px",
                color: "rgba(255,255,255,0.55)",
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              Cirurgiã-Dentista especializada em Harmonização e Estética Facial. Resultados naturais com segurança clínica.
            </p>
          </div>

          {/* Address */}
          <div>
            <h4
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: C.gold,
                marginBottom: "16px",
              }}
            >
              Localização
            </h4>

            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "18px",
                padding: "18px 18px 16px",
                backdropFilter: "blur(6px)",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "14px" }}>
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "12px",
                    backgroundColor: "rgba(200,168,75,0.14)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: C.gold,
                    fontSize: "18px",
                  }}
                >
                  📍
                </div>

                <div>
                  <div
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "18px",
                      color: "#fff",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    Cuiabá, Mato Grosso
                  </div>
                  <p
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: "13px",
                      lineHeight: 1.6,
                      color: "rgba(255,255,255,0.62)",
                      margin: 0,
                    }}
                  >
                    Atendimento em localização de fácil acesso, pensado para oferecer conforto, discrição e praticidade na sua visita.
                  </p>
                </div>
              </div>

              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  textDecoration: "none",
                  backgroundColor: C.gold,
                  color: "#fff",
                  padding: "11px 18px",
                  borderRadius: "100px",
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  boxShadow: "0 8px 24px rgba(200,168,75,0.24)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.transform = "translateY(-2px)";
                  el.style.backgroundColor = C.goldDark;
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.transform = "translateY(0)";
                  el.style.backgroundColor = C.gold;
                }}
              >
                Abrir no Maps →
              </a>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: C.gold,
                marginBottom: "16px",
              }}
            >
              Horário de Atendimento
            </h4>
            <div
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "13px",
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.8,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
                <span>Segunda a Sábado</span>
                <span style={{ color: "#fff", fontWeight: 600 }}>8h às 18h</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
                <span>Domingo</span>
                <span style={{ color: "rgba(255,255,255,0.45)" }}>Fechado</span>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: C.gold,
                marginBottom: "16px",
              }}
            >
              Contato
            </h4>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("footer_contact")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: C.green,
                color: "#fff",
                padding: "12px 20px",
                borderRadius: "100px",
                fontSize: "13px",
                fontWeight: 700,
                fontFamily: "'Montserrat', sans-serif",
                textDecoration: "none",
                transition: "opacity 0.2s ease",
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.85")}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
            >
              <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M16 4C9.373 4 4 9.373 4 16c0 2.25.605 4.36 1.664 6.176L4 28l5.985-1.636A11.96 11.96 0 0016 28c6.627 0 12-5.373 12-12S22.627 4 16 4zm-3.47 6.667c-.19-.47-.39-.48-.57-.488L11.4 10.17c-.19 0-.49.072-.748.358-.258.286-.984.962-.984 2.345 0 1.383 1.007 2.72 1.147 2.907.14.187 1.94 3.087 4.77 4.203 2.36.932 2.834.747 3.345.7.51-.047 1.647-.673 1.88-1.322.234-.65.234-1.207.164-1.323-.07-.116-.258-.187-.54-.327-.28-.14-1.647-.813-1.903-.906-.257-.093-.444-.14-.632.14-.187.28-.725.906-.889 1.093-.163.187-.327.21-.607.07-.28-.14-1.182-.436-2.252-1.39-.833-.742-1.394-1.658-1.558-1.938-.163-.28-.017-.432.123-.57.126-.125.28-.327.42-.49.14-.163.187-.28.28-.467.094-.186.047-.35-.023-.49-.07-.14-.608-1.53-.843-2.086z" fill="white"/>
              </svg>
              WhatsApp
            </a>
          </div>
        </div>

        {/* Divider */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "28px" }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "11px",
                color: "rgba(255,255,255,0.4)",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Responsável Técnica: Dra. Thayna Ferreira — Cirurgiã-Dentista — CRO-MT 7999
            </p>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "11px",
                color: "rgba(255,255,255,0.3)",
                margin: 0,
              }}
            >
              © 2026 Dra. Thayna Ferreira · Todos os direitos reservados
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ */
/*  MAIN PAGE                                                           */
/* @section: main */
/* ------------------------------------------------------------------ */
export default function Home() {
  return (
    <div style={{ backgroundColor: C.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header />
      <main>
        <HeroSection />
        <SymptomsSection />
        <ProceduresSection />
        <AboutSection />
        <SocialProofSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
